import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SessionExpiryModal } from '../shared/components/SessionExpiryModal';
import { AuthContext } from './auth';

// @ts-ignore
const SESSION_TEST_MODE = import.meta.env.VITE_SESSION_EXPIRY_TEST_MODE === 'true';
const WARNING_BEFORE_EXPIRY_MS = SESSION_TEST_MODE ? 30 * 1000 : 5 * 60 * 1000;
const CHECK_INTERVAL_MS = SESSION_TEST_MODE ? 1 * 1000 : 10 * 1000;

export const SessionExpiryProvider = ({ children }: { children: React.ReactNode }) => {
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [showModal, setShowModal] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const sessionExpiryRef = useRef<number | null>(null);

    // Initialize expiry from localStorage on mount or when user changes
    useEffect(() => {
        if (user) {
            const storedExpiry = localStorage.getItem('expiry');
            if (storedExpiry) {
                sessionExpiryRef.current = new Date(storedExpiry).getTime();
            }
        } else {
            sessionExpiryRef.current = null;
            setShowModal(false);
            setIsExpired(false);
        }
    }, [user]);

    // Listen for session-activity events (from successful API calls) to extend expiry
    useEffect(() => {
        const handleActivity = () => {
            const storedTTL = localStorage.getItem('sessionTTL');
            if (storedTTL && sessionExpiryRef.current) {
                const newExpiry = Date.now() + parseInt(storedTTL, 10);
                sessionExpiryRef.current = newExpiry;
                // If modal was showing a warning (not yet expired), hide it
                if (showModal && !isExpired) {
                    setShowModal(false);
                    setIsExpired(false);
                }
            }
        };

        window.addEventListener('session-activity', handleActivity);
        return () => window.removeEventListener('session-activity', handleActivity);
    }, [showModal, isExpired]);

    // Listen for session-expired events (from 401 responses) — redirect to login
    useEffect(() => {
        const handleSessionExpired = () => {
            console.log('[Session] 401 session-expired event received');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('expiry');
            localStorage.removeItem('sessionTTL');
            localStorage.setItem('sessionLogoutMessage', 'You have been logged out due to inactivity.');
            console.log('[Session] Redirecting to /login');
            window.location.href = '/login';
        };

        window.addEventListener('session-expired', handleSessionExpired);
        return () => window.removeEventListener('session-expired', handleSessionExpired);
    }, [userDispatch]);

    // Interval to check if we should show the warning modal or redirect on full expiry
    useEffect(() => {
        if (!user || !sessionExpiryRef.current) return;

        const interval = setInterval(() => {
            const now = Date.now();
            const expiresAt = sessionExpiryRef.current;
            if (!expiresAt) return;

            const msRemaining = expiresAt - now;

            if (msRemaining <= 0) {
                // Token has fully expired — clear auth and redirect to login
                console.log('[Session] Token expired, ms remaining:', msRemaining);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('expiry');
                localStorage.removeItem('sessionTTL');
                localStorage.setItem('sessionLogoutMessage', 'You have been logged out due to inactivity.');
                console.log('[Session] Redirecting to /login');
                window.location.href = '/login';
                return; // stop interval processing
            } else if (msRemaining <= WARNING_BEFORE_EXPIRY_MS) {
                console.log('[Session] Warning threshold reached, ms remaining:', msRemaining);
                setShowModal(true);
                setIsExpired(false);
            }
        }, CHECK_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [user, userDispatch]);

    const handleReAuthenticated = useCallback((resp: any) => {
        // Update auth context with new user data
        userDispatch({
            type: 'set_user',
            user: resp.user,
            person: resp.person,
        });

        // Update session expiry
        if (resp.expiry) {
            const newExpiry = new Date(resp.expiry).getTime();
            sessionExpiryRef.current = newExpiry;
        }

        // Dismiss modal
        setShowModal(false);
        setIsExpired(false);
    }, [userDispatch]);

    const userEmail = user?.email || user?.username || '';

    return (
        <>
            {children}
            <SessionExpiryModal
                isOpen={showModal}
                isExpired={isExpired}
                userEmail={userEmail}
                onReAuthenticated={handleReAuthenticated}
            />
        </>
    );
};
